/**
 * Parses agent message strings for AI Stylist "look" blocks.
 *
 * Supported block format:
 *   [LOOK:{...json...}]
 *
 * The JSON payload contains nested arrays AND objects (a `slots` array
 * whose entries each carry a `product` object), so a flat regex like
 * `\[LOOK:(\{[^[\]]*\})\]` is impossible — the inner `[`/`]` of the
 * slots array would terminate the match early. We use the same
 * brace-balanced parser as orderBlocks.ts: track quote/escape state,
 * count `{`/`}` depth, require the trailing `]` once depth returns to
 * zero. Square brackets inside the payload are ignored entirely because
 * we anchor on the leading `{` and balance on braces only.
 *
 * Tolerates JSON parse failures by returning the original marker text
 * verbatim, never throws.
 *
 * Each slot's `product` reuses the EXACT shape PRODUCT_CARD consumes
 * (ProductCardData), so the existing <ProductCard> renders it directly.
 */

import type { ProductCardData } from './productBlocks';

export interface LookSlot {
  role: string;        // e.g. "top", "bottom", "shoes", "accessory"
  why: string;         // one-line rationale for this piece
  product: ProductCardData;
}

export interface LookCardData {
  title: string;
  intro?: string;
  slots: LookSlot[];
  outro?: string;
}

export type LookSegment =
  | { type: 'text'; content: string }
  | { type: 'look_card'; data: LookCardData };

interface LookMarker {
  start: number;   // index of the leading '['
  end: number;     // index AFTER the trailing ']'
  jsonStr: string; // the `{...}` payload, ready for JSON.parse
}

const PREFIX = '[LOOK:';

function findLookMarkers(text: string): LookMarker[] {
  const out: LookMarker[] = [];
  let pos = 0;

  while (true) {
    const idx = text.indexOf(PREFIX, pos);
    if (idx === -1) break;
    const jsonStart = idx + PREFIX.length;
    if (text[jsonStart] !== '{') {
      pos = jsonStart;
      continue;
    }

    let depth = 0;
    let inString = false;
    let escape = false;
    let i = jsonStart;
    let jsonEnd = -1;
    for (; i < text.length; i++) {
      const c = text[i];
      if (escape) { escape = false; continue; }
      if (c === '\\') { escape = true; continue; }
      if (c === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (c === '{') depth++;
      else if (c === '}') {
        depth--;
        if (depth === 0) { jsonEnd = i + 1; break; }
      }
    }
    if (jsonEnd === -1) { pos = i; continue; }              // unbalanced — skip
    if (text[jsonEnd] !== ']') { pos = jsonEnd; continue; } // missing closing bracket

    out.push({
      start: idx,
      end: jsonEnd + 1,
      jsonStr: text.substring(jsonStart, jsonEnd),
    });
    pos = jsonEnd + 1;
  }

  return out;
}

function normalizeLook(data: LookCardData): LookCardData {
  return {
    title: data.title,
    intro: data.intro,
    outro: data.outro,
    slots: Array.isArray(data.slots) ? data.slots : [],
  };
}

export function parseLookBlocks(message: string): {
  cleanText: string;
  cards: LookCardData[];
} {
  const cards: LookCardData[] = [];
  let cleanText = '';
  let lastIndex = 0;

  for (const marker of findLookMarkers(message)) {
    if (marker.start > lastIndex) {
      cleanText += message.slice(lastIndex, marker.start);
    }
    try {
      const data = JSON.parse(marker.jsonStr) as LookCardData;
      cards.push(normalizeLook(data));
    } catch {
      cleanText += message.substring(marker.start, marker.end);
    }
    lastIndex = marker.end;
  }

  if (lastIndex < message.length) {
    cleanText += message.slice(lastIndex);
  }

  if (cards.length === 0 && cleanText === '') {
    cleanText = message;
  }

  return { cleanText, cards };
}

/**
 * Segment-style parser for interleaved rendering — preserves the
 * original order of text vs. look cards within a single message bubble,
 * same shape as parseOrderSegments.
 */
export function parseLookSegments(message: string): LookSegment[] {
  const segments: LookSegment[] = [];
  let lastIndex = 0;

  for (const marker of findLookMarkers(message)) {
    if (marker.start > lastIndex) {
      segments.push({ type: 'text', content: message.slice(lastIndex, marker.start) });
    }
    try {
      const data = JSON.parse(marker.jsonStr) as LookCardData;
      segments.push({ type: 'look_card', data: normalizeLook(data) });
    } catch {
      segments.push({ type: 'text', content: message.substring(marker.start, marker.end) });
    }
    lastIndex = marker.end;
  }

  if (lastIndex < message.length) {
    segments.push({ type: 'text', content: message.slice(lastIndex) });
  }

  if (segments.length === 0) {
    return [{ type: 'text', content: message }];
  }

  return segments;
}

/**
 * Brace-balanced strip of `[LOOK:{...}]` markers for the voice TTS path.
 * The naive `\[LOOK:\{[^[\]]*\}\]` regex used for flat markers cannot be
 * used here because LOOK contains nested `[`/`]`. Returns the message
 * with all LOOK markers removed (parse failures are still removed —
 * the voice path should never speak raw markers).
 */
export function stripLookMarkers(text: string): string {
  if (!text) return text;
  const markers = findLookMarkers(text);
  if (markers.length === 0) return text;
  let out = '';
  let lastIndex = 0;
  for (const marker of markers) {
    out += text.slice(lastIndex, marker.start);
    lastIndex = marker.end;
  }
  out += text.slice(lastIndex);
  return out;
}
